import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCallback, useMemo } from "react";
import { Compare } from "@emberly/zenith-client";
import { FixNumber } from "../../../../common/orders";
import { getCurrencyShorthand } from "../../../../providers/StationProvider";
import EntityTable from "../../../common/tables/EntityTable";
import FlagChip from "../../../common/core/FlagChip";
import { TimeView } from "../../../common/core/Time";

const Query = {
  name: "invoice_exports",
  path: "orderCount",
  comparer: Compare.GT,
  value: 0
};

export default function InvoiceJournalsSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = useMemo(() => {
    return [
      {
        field: "number",
        type: "number",
        headerName: t("invoiceJournals:fieldNumber"),
        flex: 0
      },
      {
        field: "created",
        type: "dateTime",
        headerName: t("common:created"),
        valueGetter: (_, row) => new Date(row.created),
        renderCell: (params) => <TimeView time={params.row.created} />,
        minWidth: 140
      },
      {
        field: "journalLineCount",
        type: "number",
        headerName: t("invoiceJournals:fieldJournalLineCount"),
        minWidth: 120,
      },
      {
        field: "orderCount",
        type: "number",
        headerName: t("invoiceJournals:fieldOrderCount"),
        minWidth: 120,
      },
      {
        field: "totalSum.value",
        type: "number",
        entityType: "decimal",
        valueGetter: (_, row) => Number(row.totalSum?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => `${FixNumber(params.row.totalSum?.value)} ${getCurrencyShorthand(t, params.row.totalSum?.currency)}`,
        headerName: t("invoiceJournals:fieldTotalSum"),
        minWidth: 100,
        flex: 0.5,
      },
      {
        field: "sender.name",
        valueGetter: (_, row) => row.sender?.name || "-",
        headerName: t("invoiceJournals:fieldSender"),
        minWidth: 100,
        flex: 0.5,
      },
      {
        field: "exported",
        type: "singleSelect",
        headerName: t("invoiceJournals:fieldState"),
        minWidth: 120,
        flex: 0.75,
        renderCell: (params) => <FlagChip value={params.value} label={params.value ? t("common:exported") : t("common:notExported")} />,
        valueOptions: [
          { label: t("common:exported"), value: true },
          { label: t("common:notExported"), value: false },
        ]
      }
    ]
  }, [t]);

  const onRowClick = useCallback((params, ev) => {
    navigate(`./${params.id}`, { relative: "path" });
  }, [navigate]);

  return (
    <EntityTable
      tableId="invoice_exports"
      type="InvoiceExport"
      filter={Query}
      onRowClick={onRowClick}
      columns={columns}
      emptyStateTitle={t("invoiceJournals:emptyStateTitle")}
      emptyStateDescription={t("invoiceJournals:emptyStateDescription")}
      serverSidePaging
      sortDescendingByDefault
      xs={12}
    />
  );
}
